import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getFeedbackFormData } from '../../services/scpApi';
import { attendanceScores, effortScores, achievmentScores } from '../../data/data';
import { saveFeedbackForm, submitCombinedFeedbackForm } from '../../services/scpApi';
import { formatDate } from '../../utils/dateHelpers';
import DatePicker from "react-datepicker";
import CustomSelect from "../customSelect/CustomSelect";
import SaveButton from '../saveButton/SaveButton';
import DocumentUpload from '../documentUpload/DocumentUpload';
import FeedbackFormPdf from '../feedbackFormPdf/FeedbackFormPdf';
import cross from '../../assets/x.svg';
import "react-datepicker/dist/react-datepicker.css";

const FeedbackForm = () => {

    const [formData, setFormData] = useState({});
    const [validationErrors, setValidationErrors] = useState([]);
    const [combinedValidationErrors, setCombinedValidationErrors] = useState([]);
    const [successMessages, setSuccessMessages] = useState([]);
    const [combinedSuccessMessages, setCombinedSuccessMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saveDraftLoading, setSaveDraftLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [combinedSubmitLoading, setCombinedSubmitLoading] = useState(false);
    const [formDisabled, setFormDisabled] = useState(false);

    const [selectedAssessmentDocumentFile, setSelectedAssessmentDocumentFile] = useState({});
    const [selectedCombinedDocumentFile, setSelectedCombinedDocumentFile] = useState({});

    const feedbackFormRef = useRef();

    const selectedStudent = useSelector(state => state.portalSelected.selectedStudent);
    const authUser = useSelector(state => state.auth.user);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            setLoading(true);

            if (!selectedStudent) {
                return;
            }

            // get the feedback form id from the url path
            const path = window.location.pathname;
            const feedbackFormId = path.split('/').pop();

            // console.log("Feedback Form loading...", feedbackFormId, "for student:", selectedStudent.id);

            // fetch the feedback form data if needed
            try {
                const feedbackFormData = await getFeedbackFormData({
                    pupilId: selectedStudent.id,
                    feedbackFormId: feedbackFormId
                });
                const feedbackForm = feedbackFormData.feedback_form;
                feedbackForm.assessment_data = JSON.parse(feedbackForm.assessment_data);
                setFormData(feedbackForm);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching feedback form data:", error);
            }
        })();
    }, [selectedStudent]);

    /**
     * Handle input change for general form fields 
     */
    const handleChange = (e) => {
        setValidationErrors([]);

        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    /**
     * Handle lesson change
     */
    const handleLessonChange = (index, name, value) => {
        setValidationErrors([]);

        setFormData(prevData => {
            const updatedLessons = [...prevData.assessment_data.lessons];

            updatedLessons[index] = {
                ...updatedLessons[index],
                [name]: value
            };

            // console.log("Updated Lesson:", updatedLessons[index]);

            return {
                ...prevData,
                assessment_data: {
                    ...prevData.assessment_data,
                    lessons: updatedLessons
                }
            };
        });
    }

    /**
     * Handle assessment changes
     */
    const handleAssessmentChange = (index, value) => {
        setValidationErrors([]);

        setFormData(prevData => {
            // clone existing sections
            const updatedSections = prevData.assessment_data.assessment.sections.map((section, i) =>
                i === index
                    ? { ...section, section_pass: value }
                    : section
            );

            return {
                ...prevData,
                assessment_data: {
                    ...prevData.assessment_data,
                    assessment: {
                        ...prevData.assessment_data.assessment,
                        sections: updatedSections
                    }
                }
            };
        });
    };

    /**
     * Handle assessment date change
     */
    const handleAssessmentDataChange = (name, value) => {
        setValidationErrors([]);

        setFormData(prevData => ({
            ...prevData,
            assessment_data: {
                ...prevData.assessment_data,
                assessment: {
                    ...prevData.assessment_data.assessment,
                    [name]: value
                }
            }
        }));
    };

    // /**
    //  * Handle assessment comment change
    //  */
     
    //  const handleAssessmentCommentChange = (value) => {
    //     setValidationErrors([]);

    //     setFormData(prevData => ({
    //         ...prevData,
    //         assessment_data: {
    //             ...prevData.assessment_data,
    //             assessment: {

    //             }
    //         }
    //     }))
    // }

    /**
     * Handle overview feedback change
     */
    const handleOverviewFeedbackChange = (e) => {
        setValidationErrors([]);

        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            assessment_data: {
                ...prevData.assessment_data,
                overview_feedback: {
                    ...prevData.assessment_data.overview_feedback,
                    [name]: value
                }
            }
        }));
    };

    /**
     * Handle save draft
     */
    const handleSave = async (e, submit = false) => {
        e.preventDefault();
        
        if (submit) {
            setSubmitLoading(true);
            if (!formData?.documentFile && !formData?.document_id && !selectedAssessmentDocumentFile?.file) {
                // pop up choice to let user know they are submitting without a document
                const confirm = window.confirm('You are submitting without a document. Do you want to continue?');
                if (!confirm) {
                    setSubmitLoading(false);
                    return;
                }
            }
            if (formData.submitted_date) {
                // pop up if they are re-submitting 
                const confirmResubmit = window.confirm('You are re-submitting a feedback form. Do you want to continue?');
                if (!confirmResubmit) {
                    setSubmitLoading(false);
                    return;
                }
        }
        } else {
            setSaveDraftLoading(true);
        }

        setValidationErrors([]);
        setSuccessMessages([]);

        const payload = {
            ...formData,
            documentFile: selectedAssessmentDocumentFile?.file ? selectedAssessmentDocumentFile.file : null,
            submit: false
        }
        
        payload.assessment_data = JSON.stringify(payload.assessment_data);

        // convert to formdata
        const formDataPayload = new FormData();
        for (const key in payload) {
            if (payload[key] !== null && payload[key] !== undefined) {
                if (key === 'documentFile') {
                    formDataPayload.append('documentFile', payload[key]);
                } else {
                    formDataPayload.append(key, payload[key]);
                }
            }
        }

        const feedbackFormId = formData.id;
        const pupilId = formData.pupil_id;

        if (submit) {
            formDataPayload.append('submit', true);
        }

        // console.log("Saving feedback form draft with payload:", feedbackFormId, pupilId, formDataPayload);

        try {
            const response = await saveFeedbackForm({feedbackFormId, pupilId, formData: formDataPayload});
            
            // success
            // console.log("Feedback form draft saved successfully:", response);

            if (submit) {
                setSuccessMessages(["Feedback form submitted successfully."]);
                // console.log("Feedback form submitted successfully.");
            } else {
                setSuccessMessages(["Draft saved successfully. You can continue editing or submit later."]);
                // console.log("Draft saved successfully.");
            }
        } catch (error) {
            console.error("Error saving feedback form draft:", error?.response?.data?.message);
            setValidationErrors([error?.response?.data?.message ? error.response.data.message : error.message]);
        }

        setSaveDraftLoading(false);
        setSubmitLoading(false);
    }

    /**
     * Handle final submit
     */
    const handleCombinedSubmit = async (e) => {
        e.preventDefault();

        setCombinedSubmitLoading(true);
        setCombinedValidationErrors([]);
        setCombinedSuccessMessages([]);

        const documentFile = selectedCombinedDocumentFile?.file ? selectedCombinedDocumentFile.file : null;

        const formDataPayload = new FormData();
        formDataPayload.append('documentFile', documentFile);

        const feedbackFormId = formData.id;
        const pupilId = formData.pupil_id;

        // console.log("Submitting combined document with payload:", feedbackFormId, pupilId, formDataPayload);

        try {
            const response = await submitCombinedFeedbackForm({feedbackFormId, pupilId, formData: formDataPayload});
            // console.log("Combined feedback form submitted successfully:", response);
            setCombinedSuccessMessages(["Combined assessment and feedback form submitted successfully."]);
        } catch (error) {
            console.error("Error saving feedback form draft:", error.response.data.message);
            setCombinedValidationErrors([error.response.data.message]);
        }

        setCombinedSubmitLoading(false);
    }

    return (
        <>
        {formData?.assessment_data && !formDisabled ? (
            <div className={`${loading && 'loading'} feedback-form`}>
                <div className="controll-btn-cont">
                    <p onClick={() => navigate(-1)} className="link close-button">
                        <img className="cross" src={require('../../assets/x.svg').default} alt="back" />
                        Close
                    </p>                    
                </div>
                <div className="flex-row align-center">

                    {formData.submitted_date ? (
                        <h3 className="submitted-alert">Feedback has been submitted: {formData.submitted_date}.</h3>
                    ) : (
                        <h3>Complete and submit electronic assessment feedback</h3>
                    )}
                    <FeedbackFormPdf 
                        feedbackFormRef={feedbackFormRef}
                        feedbackFormPdfName={`feedback_form_${formData.student_name}_${formData.id}.pdf`}
                    />
                </div>
                <div className="pdf-cont" ref={feedbackFormRef}>
                    <div className='outer-cont'>
                        <div className='inner-cont'>
                            <p className='bold'>School: <span>{formData.school_name}</span></p>
                            <p className='bold'>Student Name: <span>{formData.student_name}</span></p>
                            <p className='bold'>Year Group: <span>{formData.year_group}</span></p>
                        </div>

                        <div className='inner-cont'>
                            <p className='bold'>Start date: <span>{formatDate(formData.start_date)}</span></p>
                            <p className='bold'>Submitted date: <span>{formatDate(formData.submitted_date)}</span></p>
                        </div>

                        <div className='inner-cont inputs'>
                            <div className='input-row'>
                            <div>
                                <p className='bold'>Tutor group: <span>{formData.tutor_group_name}</span></p>
                            </div>
                            <div>
                                <label className='instruction'>
                                    Absences (List Dates):
                                    <input
                                        type="text"
                                        name="absences"
                                        value={formData.absences ?? ''}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                            </div>
                            </div>

                            <div className='input-row'>
                            <div>
                                <p className='bold'>Tutor group lead: <span>{formData.tutor_group_lead_name}</span></p>
                            </div>

                            <div>
                                <label className='instruction'>
                                    No. of lessons per week:
                                    <input
                                        type="text"
                                        name="number_of_lessons_per_week"
                                        value={formData.number_of_lessons_per_week ?? ''}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                            </div>
                            </div>
                        </div>
                    </div>

                    <div className='outer-cont-col'>
                        <div className='assessments-heading'>
                            <div className='flex-row'>
                                <p className='bold'>Bundle: <span>{formData.bundle_label}</span></p>
                                <p className='bold'>Level {formData.level}</p>
                            </div>
                        </div>

                        <p className="outcome-statement"><span>{formData.bundle_outcome}</span></p>

                        <div className='outer-lesson-cont'>
                            {formData.assessment_data?.lessons && formData.assessment_data.lessons.map((assessment, index) => (
                                <div className={`lesson-cont ${assessment.enabled}`}>
                                    <p>Tutorial {index + 1}</p>
                                    <label>Date:</label>
                                    <DatePicker
                                        selected={assessment.date || null}
                                        onChange={(date) => handleLessonChange(index, 'date', date)}
                                        dateFormat="dd/MM/yyyy"
                                        required
                                    />

                                    <label>
                                        Achieved 2 stars independantly?
                                        <br/>
                                        <div className="switch">
                                            <input 
                                                type="checkbox"
                                                name="achieved_two_stars_independently"
                                                checked={assessment.achieved_two_stars_independently} 
                                                onClick={(e) => handleLessonChange(index, e.target.name, e.target.checked)}
                                                onChange={(e) => handleLessonChange(index, e.target.name, e.target.checked)}
                                            />
                                            <span className="slider round"></span>
                                        </div>
                                    </label>

                                    <label>
                                        Comments:                           
                                        <textarea
                                            name="comments"
                                            value={assessment.comments ?? ''}
                                            onChange={(e) => handleLessonChange(index, e.target.name, e.target.value)}
                                            required
                                        ></textarea>
                                    </label>
                                </div>
                            ))}
                            
                            { formData?.assessment_data?.assessment
                            && formData?.assessment_data?.assessment?.needed 
                            ? (
                            <div className={`lesson-cont ${formData?.assessment_data?.assessment?.sections?.length > 2 ? 'larger' : ''}`}>
                                <p>Assessment</p>
                                <label>Date:</label>

                                <DatePicker
                                    selected={formData?.assessment_data?.assessment?.date || null}
                                    onChange={(date) => handleAssessmentDataChange('date', date)}
                                    dateFormat="dd/MM/yyyy"
                                    required
                                />

                                <div className='slider-cont'>
                                    {formData?.assessment_data?.assessment?.sections?.length > 0 &&
                                    formData.assessment_data.assessment.sections.map((section, index) => (
                                        <label key={index} className='flex-col-label'>
                                            <b>Section {index + 1} Pass: </b><span className='section-name-span'>{section?.name}</span>
                                            <div className="switch">
                                            <input 
                                                type="checkbox"
                                                checked={section.section_pass} 
                                                onChange={(e) => handleAssessmentChange(index, e.target.checked)}
                                            />
                                            <span className="slider round"></span>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                <label>
                                    Comments:                           
                                    <textarea
                                        name="comments"
                                        value={formData.assessment_data.assessment.comments ?? ''}
                                        onChange={(e) => handleAssessmentDataChange(e.target.name, e.target.value)}
                                        required
                                    ></textarea>
                                </label>

                            </div>)
                            : (
                            <div className={`lesson-cont`}>
                                <p>Assessment</p>
                                <small>No Assessment is needed for this topic as there is only one tutorial. Upload the 2-start work instead</small>
                            </div>
                            )}
                        </div>
                    </div>

                    <div className='outer-cont'>
                        <div>
                            <p>Overview Feedback</p>

                            <div className='flex-row'>
                                <div>
                                    <p className='instruction'>Attendance 1=excellent, 2=good, 3=average, 4=poor</p>
                                    <CustomSelect
                                        name="attendance"
                                        placeholder="Select Attendance"
                                        options={attendanceScores}
                                        labelKey="name"
                                        setSelectedOption={(option) => handleOverviewFeedbackChange({ target: { name: 'attendance', value: option.id } })}
                                        selectedOption={attendanceScores.find(attendanceScores => attendanceScores.id === formData.assessment_data.overview_feedback.attendance) || {}}
                                    />
                                </div>
                                
                                <div>
                                    <p className='instruction'>Effort 1=excellent, 2=good, 3=average, 4=poor</p>
                                    <CustomSelect
                                        name="effort"
                                        placeholder="Select Effort"
                                        options={effortScores}
                                        labelKey="name"
                                        setSelectedOption={(option) => handleOverviewFeedbackChange({ target: { name: 'effort', value: option.id } })}
                                        selectedOption={effortScores.find(effortScores => effortScores.id === formData.assessment_data.overview_feedback.effort) || {}}
                                    />
                                </div>

                                <div>
                                    <p className='instruction'>Achievement: WT=Working towards expected (still gaps), E=Expected (achieved - move on)</p>
                                    <CustomSelect
                                        name="achievement"
                                        placeholder="Select Achievement"
                                        options={achievmentScores}
                                        labelKey="name"
                                        setSelectedOption={(option) => handleOverviewFeedbackChange({ target: { name: 'achievement', value: option.id } })}
                                        selectedOption={achievmentScores.find(achievmentScores => achievmentScores.id === formData.assessment_data.overview_feedback.achievement) || {}}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional general_notes field */}
                    <div className='outer-cont'>
                        <div className='inner-cont additional-notes'>
                            <p>Additional General Notes</p>

                            <div className='input-row'>
                                <label className='width-100'>
                                    <textarea
                                        className='general-notes'
                                        name="general_notes"
                                        value={formData.general_notes ?? ''}
                                        onChange={(e) => handleChange(e)}
                                        required
                                    ></textarea>
                                </label>
                            </div>
                        </div>
                    </div>

                </div>

                <div className='outer-cont'>
                    <div className='inner-cont'>
                    <p>Assessment Document Upload</p>
                    <p className='instruction'>Upload the students completed assessment to attach to the electronic feedback form.</p>
                        {formData.document_id && 
                            <p className="instruction red">You have already uploaded a document, for this feedback form. Upload a new one to replace the previous.</p>
                        }
                        <DocumentUpload
                            inputKey='document-upload'
                            selectedDocumentFile={selectedAssessmentDocumentFile}
                            setSelectedDocumentFile={setSelectedAssessmentDocumentFile}
                            setValidationErrors={setValidationErrors}
                        />
                    </div>
                </div>

                <div className='button-cont'>
                    <SaveButton
                        save={(e) => handleSave(e)}
                        loading={saveDraftLoading ? 'loading': ''}
                        textUpdate="Save draft"
                        textUpdateLoading="Saving..."
                        textUpdateSuccess="Saved!"
                        textUpdateError="Error"
                        isUpdating={true}
                    />

                    <SaveButton
                        save={(e) => handleSave(e, true)}
                        loading={submitLoading ? 'loading' : ''}
                        textUpdate="Submit"
                        textUpdateLoading="Submitting..."
                        textUpdateSuccess="Submitted!"
                        textUpdateError="Error"
                        isUpdating={true}
                    />
                </div>

                {validationErrors && validationErrors.length > 0 &&
                    validationErrors.map((error, index) => (
                        <div className='error-messages'>
                            <p key={index} className='error'>{error}</p>
                        </div>
                    ))
                }
                
                {successMessages && successMessages.length > 0 &&
                    successMessages.map((message, index) => (
                        <div className='success-messages'>
                            <p key={index} className='success'>{message}</p>
                        </div>
                    ))
                }

                <h3 className='separator-title'>Or complete and upload manually</h3>

                <p className='instruction'>Use this option to download and complete the assessment feedback form manually.</p>
                <p className='instruction'>Please ensure that you upload the feedback form and student’s completed assessment as one scanned document.</p>
                
                <div className='outer-cont'>
                    <div className='inner-cont'>
                        <p>Combined Feedback & Assessment Document Upload</p>
                        <p className='instruction'>Upload the combined document.</p>

                        {formData.combined_document_id && 
                            <p className="instruction red">You have already uploaded a document, for this feedback form. Upload a new one to replace the previous.</p>
                        }

                        <DocumentUpload
                            inputKey='combined-document-upload'
                            selectedDocumentFile={selectedCombinedDocumentFile}
                            setSelectedDocumentFile={setSelectedCombinedDocumentFile}
                            setValidationErrors={setValidationErrors}
                        />
                    </div>
                </div>

                <div className='button-cont'>
                    <FeedbackFormPdf 
                        feedbackFormRef={feedbackFormRef}
                        feedbackFormPdfName={`feedback_form_${formData.student_name}_${formData.id}.pdf`}
                    />

                    <SaveButton
                        save={(e) => handleCombinedSubmit(e, true)}
                        loading={combinedSubmitLoading ? 'loading' : ''}
                        textUpdate="Submit combined document"
                        textUpdateLoading="Submitting..."
                        textUpdateSuccess="Submitted!"
                        textUpdateError="Error"
                        isUpdating={true}
                    />
                </div>

                {combinedValidationErrors && combinedValidationErrors.length > 0 &&
                    combinedValidationErrors.map((error, index) => (
                        <div className='error-messages'>
                            <p key={index} className='error'>{error}</p>
                        </div>
                    ))
                }
                
                {combinedSuccessMessages && combinedSuccessMessages.length > 0 &&
                    combinedSuccessMessages.map((message, index) => (
                        <div className='success-messages'>
                            <p key={index} className='success'>{message}</p>
                        </div>
                    ))
                }

            </div>
        ) : (formData.submitted_date) ? (
            <div className='feedback-form'>
                <p className="center">Nothing to see here! Feedback has already been submitted: {formData.submitted_date}.</p>
            </div>
        ) : (
            <div>

            </div>
        )}
        </>
        
    );
}

export default FeedbackForm;
